import Icon from "components/general/Icon";
import Label from "components/general/Label";
import React from "react";
import Select from "react-select";
import iconCheck from "images/ic_check_black.png";
import iconDown from "images/ic_down_black.png";
import styled from "styled-components";
import theme from "styles/theme";

const Container = styled.div`
  margin-bottom: ${(props) =>
    props.$marginBottom ? props.$marginBottom : 0}px;

  font-family: Inter;
  font-style: normal;
  font-weight: ${(props) => (props.$isSmall ? "500" : "normal")};
  font-size: 14px;
  line-height: 17px;
`;

const StyledLabel = styled(Label)`
  padding-bottom: ${theme.spacing.unit * 1}px;
`;

const Arrow = styled.img`
  display: block;
  padding-right: 16px;
`;

const Error = styled.div`
  margin-top: ${theme.spacing.unit * 1}px;

  // Text
  font-family: Inter;
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 15px;
  color: ${theme.colors.red};
`;

const OptionContainer = styled.div`
  display: flex;
  align-items: center;
  box-sizing: border-box;
  height: 56px;
  width: 100%;
  margin: 4px 0;
  padding: 0 ${theme.spacing.unit * 1}px;

  color: ${theme.colors.gray3};
`;

const Option = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-sizing: border-box;
  height: 100%;
  width: 100%;
  border-radius: 8px;

  ${(props) =>
    props.$isChild &&
    `
    padding-left: ${theme.spacing.unit * 2}px;
  `}

  ${(props) =>
    props.$isFocused &&
    !props.$isDisabled &&
    `
    text-decoration: underline;
    background-color: ${theme.colors.gray1};
  `}

  ${(props) =>
    !props.$isDisabled &&
    `
    &:hover {
      cursor: pointer;
      text-decoration: underline;
      background-color: ${theme.colors.gray1};
    }
  `}

  ${(props) =>
    props.$isDisabled &&
    `
      opacity: 0.5;
  `}
`;

const OptionLabel = styled.div`
  padding: 0 ${theme.spacing.unit * 2}px;
`;

const OptionCheckmark = styled(Icon)`
  margin-right: ${theme.spacing.unit * 1}px;
`;

const CustomDropdownIndicator = ({ innerProps }) => (
  <div {...innerProps}>
    <Arrow src={iconDown} width="24" height="24" alt="" />
  </div>
);

const CustomOption = ({
  isFocused,
  isSelected,
  isDisabled,
  label,
  value,
  innerProps,
  innerRef,
  selectProps,
}) => {
  let isChild = false;
  let isParentSelected = false;
  if (selectProps.nestingData) {
    isChild = selectProps.nestingData[value]?.isChild;
    isParentSelected = selectProps.nestingData[value]?.isParentSelected;
  }

  return (
    <OptionContainer {...innerProps} ref={innerRef}>
      <Option
        $isFocused={isFocused}
        $isDisabled={isDisabled}
        $isChild={isChild}
      >
        <OptionLabel>{label}</OptionLabel>
        {(isSelected || isParentSelected) && (
          <OptionCheckmark src={iconCheck} />
        )}
      </Option>
    </OptionContainer>
  );
};

const customStyles = {
  control: (provided, state) => {
    let borderColor =
      state.menuIsOpen || state.isFocused
        ? theme.colors.gray4
        : theme.colors.gray2;
    if (state.selectProps.hasError) {
      borderColor = theme.colors.red;
    }
    const borderStyle = "solid";
    const borderWidth = state.menuIsOpen ? "2px 2px 0 2px" : "2px";
    const borderRadius = state.menuIsOpen ? "16px 16px 0 0" : "16px";
    const paddingBottom = state.menuIsOpen ? "2px" : 0;

    return {
      ...provided,
      minHeight: "56px",
      borderColor,
      borderStyle,
      borderWidth,
      borderRadius,
      paddingBottom,
      boxSizing: "border-box",
      boxShadow: "none",
      ":hover": {
        borderColor: state.selectProps.hasError
          ? theme.colors.red
          : theme.colors.gray4,
        cursor: "pointer",
      },
    };
  },
  valueContainer: (provided, state) => ({
    ...provided,
    paddingLeft: `${theme.spacing.unit * 2}px`,
    paddingRight: `${theme.spacing.unit * 2}px`,
  }),
  indicatorSeparator: (provided, state) => ({
    display: "none",
  }),
  menu: (provided, state) => {
    const borderColor = state.selectProps.hasError
      ? theme.colors.red
      : theme.colors.gray4;
    const border = `2px solid ${borderColor}`;
    const borderRadius = "0 0 16px 16px";

    return {
      ...provided,
      border,
      borderRadius,
      boxShadow: "none",
      marginTop: 0,
      marginBottom: 0,
      borderTop: 0,
      overflow: "hidden",
    };
  },
  menuList: (provided, state) => ({
    ...provided,
    paddingTop: 0,
  }),
  // option: (provided, state) => {
  //   const color = state.isDisabled ? theme.colors.gray3 : theme.colors.gray4;
  //   const backgroundColor =
  //     state.isFocused || state.isSelected
  //       ? theme.colors.gray2
  //       : theme.colors.white;
  //   const helperContent =
  //     state.isDisabled && state.label !== "..." ? "'Coming Soon'" : "''";

  //   return {
  //     ...provided,
  //     color: color,
  //     backgroundColor,
  //     padding: `19px ${theme.spacing.unit * 2}px`,
  //     ":hover": {
  //       cursor: state.isDisabled ? "auto" : "pointer",
  //     },
  //     ":active": {
  //       backgroundColor: state.isDisabled
  //         ? backgroundColor
  //         : theme.colors.gray2,
  //     },
  //     "&::after": {
  //       content: helperContent,
  //       float: "right",
  //       color: theme.colors.gray3,
  //     },
  //   };
  // },
  placeholder: (provided, state) => ({
    ...provided,
    color: state.selectProps.hasError ? theme.colors.red : theme.colors.gray3,
  }),
};

const controlSmall = (provided, state) => {
  let borderColor = state.menuIsOpen ? theme.colors.gray4 : theme.colors.gray2;
  if (state.selectProps.hasError) {
    borderColor = theme.colors.red;
  }
  const borderStyle = "solid";
  const borderWidth = state.menuIsOpen ? "2px 2px 0 2px" : "2px";
  const borderRadius = state.menuIsOpen ? "16px 16px 0 0" : "16px";
  const paddingBottom = state.menuIsOpen ? "2px" : 0;

  return {
    ...provided,
    minHeight: "40px",
    borderColor,
    borderStyle,
    borderWidth,
    borderRadius,
    paddingBottom,
    boxSizing: "border-box",
    boxShadow: "none",
    ":hover": {
      borderColor: state.selectProps.hasError
        ? theme.colors.red
        : theme.colors.gray4,
      cursor: "pointer",
    },
  };
};

const singleValueSmall = (provided, state) => {
  return {
    ...provided,
    textOverflow: "none",
    maxWidth: "calc(100% - 24px)",
  };
};

const optionSmall = (provided, state) => {
  const color = state.isDisabled ? theme.colors.gray3 : theme.colors.gray4;
  const backgroundColor =
    state.isFocused || state.isSelected
      ? theme.colors.gray2
      : theme.colors.white;
  const helperContent =
    state.isDisabled && state.label !== "..." ? "'Coming Soon'" : "''";

  return {
    ...provided,
    color: color,
    backgroundColor,
    padding: `11px ${theme.spacing.unit * 2}px`,
    ":hover": {
      cursor: state.isDisabled ? "auto" : "pointer",
    },
    ":active": {
      backgroundColor: state.isDisabled ? backgroundColor : theme.colors.gray2,
    },
    "&::after": {
      content: helperContent,
      float: "right",
      color: theme.colors.gray3,
    },
  };
};

const PuckSelect = (props) => {
  const {
    className,
    label,
    isRequired,
    error,
    touched,
    marginBottom,
    isSmall,
    value,
    nestingData,
  } = props;

  const hasError = error && touched;

  const customStylesCopy = Object.assign({}, customStyles);
  if (isSmall) {
    customStylesCopy.control = controlSmall;
    customStylesCopy.singleValue = singleValueSmall;
    customStylesCopy.option = optionSmall;
  }

  return (
    <Container
      className={className}
      $marginBottom={marginBottom}
      $isSmall={isSmall}
    >
      {label && <StyledLabel text={label} isRequired={isRequired} />}
      <Select
        {...props}
        value={value}
        nestingData={nestingData}
        hasError={hasError}
        styles={customStylesCopy}
        components={{
          DropdownIndicator: CustomDropdownIndicator,
          Option: CustomOption,
        }}
        className="puck-select"
      />
      {hasError && <Error>{error}</Error>}
    </Container>
  );
};

export default PuckSelect;
